/* AQUON Ingredient Cards - click & drag horizontal scroll + 3D flip cards.
   Scroller: Pointer Events; pointer capture and the dragging state start only
   once the pointer has moved past a small threshold, so a plain click still
   reaches the card button underneath. Both left and right mouse buttons drag.
   Native touch swipe on mobile is preserved.
   Flip: one disclosure controller for every device (mouse, keyboard, touch):
   - the front is a real <button> (aria-expanded + aria-controls -> back panel);
     click / Enter / Space toggles it, Escape or the close button closes it;
   - the closed back is aria-hidden + inert, the open front is inert, so the
     inactive face is out of the tab order and the accessibility tree;
   - focus moves to the close button once the back is visible (CSS swaps the
     faces at the flip midpoint) and returns to the front on close.
   The asset is included once per section instance: the IIFE runs once, each
   scroller/card is bound once, and shopify:section:load is safe. */
(function () {
  'use strict';
  if (window.AquonIngredientCards) return;

  var DRAG_THRESHOLD = 5;
  var hoverMQ = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  var reduceMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var timers = typeof WeakMap === 'function' ? new WeakMap() : null;
  function getTimer(card) { return timers ? timers.get(card) : card.__aicTimer; }
  function setTimer(card, id) { if (timers) timers.set(card, id); else card.__aicTimer = id; }

  function initScroller(el) {
    if (!el || el.dataset.aicDragInit === '1') return;
    el.dataset.aicDragInit = '1';

    var isDown = false;
    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    // Block native HTML5 image drag inside the scroller
    el.querySelectorAll('img').forEach(function (img) {
      img.setAttribute('draggable', 'false');
    });

    // Suppress context menu so right-click drag works without popup
    el.addEventListener('contextmenu', function (e) {
      if (moved) e.preventDefault();
    });

    el.addEventListener('pointerdown', function (e) {
      // Ignore touch/pen: native scroll handles those
      if (e.pointerType !== 'mouse') return;
      // Accept left (0) and right (2) mouse buttons
      if (e.button !== 0 && e.button !== 2) return;

      isDown = true;
      moved = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
    });

    el.addEventListener('pointermove', function (e) {
      if (!isDown || e.pointerId !== pointerId) return;
      // Button released outside the scroller: end the gesture.
      if (!e.buttons) { release(e); return; }
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) <= DRAG_THRESHOLD) return;
        moved = true;
        el.classList.add('is-dragging');
        el.querySelectorAll('[data-aic-card]').forEach(function (card) {
          if (card.__aicHoverTimer) clearTimeout(card.__aicHoverTimer);
          if (card.__aicHoverOpen) {
            card.__aicHoverOpen = false;
            setOpen(card, false, { focus: false });
          }
        });
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      }
      el.scrollLeft = startScroll - dx;
      e.preventDefault();
    });

    function release(e) {
      if (!isDown || (e && e.pointerId !== pointerId)) return;
      isDown = false;
      el.classList.remove('is-dragging');
      try { el.releasePointerCapture(pointerId); } catch (err) {}
      pointerId = null;

      if (moved) {
        // Prevent the click / auxclick that follows a drag from firing on inner links
        var suppressClick = function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          el.removeEventListener('click', suppressClick, true);
          el.removeEventListener('auxclick', suppressClick, true);
        };
        el.addEventListener('click', suppressClick, true);
        el.addEventListener('auxclick', suppressClick, true);
        // Fallback: clear the suppressor next tick if no click fires
        setTimeout(function () {
          el.removeEventListener('click', suppressClick, true);
          el.removeEventListener('auxclick', suppressClick, true);
        }, 0);
      }
    }

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);

    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* 3D flip: front -> frosted detail back. Uses the drag suppressor above, so a
     drag never triggers a flip. */
  function parts(card) {
    return {
      front: card.querySelector('[data-aic-front]'),
      back: card.querySelector('[data-aic-back]'),
      close: card.querySelector('[data-aic-close]'),
      scroller: card.querySelector('.aic__back-content')
    };
  }

  function setInert(el, on) {
    if (!el) return;
    if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
  }

  function isOpen(card) { return card.classList.contains('is-flipped'); }

  // The CSS swaps face visibility at half the flip duration; a face cannot take
  // focus before it is visible. Reduced motion swaps instantly.
  function swapDelay(card) {
    if (reduceMQ && reduceMQ.matches) return 0;
    var raw = String(getComputedStyle(card).getPropertyValue('--aic-flip-dur') || '').trim();
    var ms = parseFloat(raw);
    if (!isFinite(ms)) ms = 700;
    else if (/[^m]s$/.test(raw)) ms *= 1000;
    return Math.max(0, ms / 2) + 20;
  }

  // Focus once the element is visible (retrying for a few frames in case the
  // visibility swap lands late), then run `done`. The outgoing face is only
  // retired in `done`, so focus never falls back to <body> in between.
  function focusThen(el, tries, done, current) {
    if (!current()) return;
    if (el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }
    if (!el || document.activeElement === el || tries <= 0) { done(); return; }
    window.requestAnimationFrame(function () { focusThen(el, tries - 1, done, current); });
  }

  // Move focus only if it is still in this card (or nowhere), never steal it.
  function focusAllowed(card) {
    var a = document.activeElement;
    return !a || a === document.body || card.contains(a);
  }

  function setOpen(card, open, opts) {
    var p = parts(card);
    if (!p.front || !p.back) return;
    opts = opts || {};
    var pending = getTimer(card);
    if (pending) clearTimeout(pending);
    var seq = (card.__aicSeq || 0) + 1;
    card.__aicSeq = seq;
    setTimer(card, 0);
    function current() { return card.__aicSeq === seq && isOpen(card) === open; }

    card.classList.toggle('is-flipped', open);
    p.front.setAttribute('aria-expanded', open ? 'true' : 'false');
    // The incoming face is released now; CSS keeps it visibility:hidden until
    // the midpoint, so it is neither seen nor reachable before it shows.
    if (open) { setInert(p.back, false); p.back.setAttribute('aria-hidden', 'false'); }
    else setInert(p.front, false);

    function retire() {
      if (!current()) return;
      if (open) {
        setInert(p.front, true);
      } else {
        if (p.scroller) { p.scroller.tabIndex = -1; p.scroller.scrollTop = 0; }
        setInert(p.back, true);
        p.back.setAttribute('aria-hidden', 'true');
      }
    }

    function finish() {
      setTimer(card, 0);
      if (!current()) return;
      if (open && p.scroller) p.scroller.tabIndex = p.scroller.scrollHeight > p.scroller.clientHeight + 1 ? 0 : -1;
      var target = open ? (p.close || p.scroller) : p.front;
      if (opts.focus !== false && focusAllowed(card)) focusThen(target, 10, retire, current);
      else retire();
    }

    var delay = opts.instant ? 0 : swapDelay(card);
    if (delay > 0) setTimer(card, setTimeout(finish, delay)); else finish();
  }

  function initFlip(card) {
    if (!card || card.dataset.aicFlipInit === '1') return;
    var p = parts(card);
    if (!p.front || !p.back) return;
    card.dataset.aicFlipInit = '1';

    var open = isOpen(card);
    p.front.setAttribute('aria-expanded', open ? 'true' : 'false');
    p.back.setAttribute('aria-hidden', open ? 'false' : 'true');
    setInert(p.back, !open);
    setInert(p.front, open);
    if (p.scroller && !open) p.scroller.tabIndex = -1;

    // Buttons fire click for mouse, touch, Enter and Space alike.
    p.front.addEventListener('click', function () {
      if (card.__aicHoverTimer) clearTimeout(card.__aicHoverTimer);
      var open = card.__aicHoverOpen || !isOpen(card);
      card.__aicHoverOpen = false;
      setOpen(card, open, { focus: true });
    });
    if (p.close) p.close.addEventListener('click', function () {
      card.__aicHoverOpen = false;
      setOpen(card, false, { focus: true });
    });
    card.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse' || e.buttons || !hoverMQ || !hoverMQ.matches || isOpen(card) || card.contains(document.activeElement)) return;
      card.__aicHoverTimer = setTimeout(function () {
        var scroller = card.closest('.aic__scroller');
        if (isOpen(card) || (scroller && scroller.classList.contains('is-dragging'))) return;
        card.__aicHoverOpen = true;
        setOpen(card, true, { focus: false });
      }, 120);
    });
    card.addEventListener('pointerdown', function () {
      if (card.__aicHoverTimer) clearTimeout(card.__aicHoverTimer);
    });
    card.addEventListener('pointerleave', function () {
      if (card.__aicHoverTimer) clearTimeout(card.__aicHoverTimer);
      if (card.__aicHoverOpen && !card.contains(document.activeElement)) {
        card.__aicHoverOpen = false;
        setOpen(card, false, { focus: false });
      }
    });
    card.addEventListener('focusout', function (e) {
      if (card.__aicHoverOpen && !card.contains(e.relatedTarget) && !card.matches(':hover')) {
        card.__aicHoverOpen = false;
        setOpen(card, false, { focus: false });
      }
    });
    card.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && isOpen(card)) {
        e.stopPropagation();
        card.__aicHoverOpen = false;
        setOpen(card, false, { focus: true });
      }
    });
  }

  function initAll(scope) {
    var s = scope && scope.querySelectorAll ? scope : document;
    s.querySelectorAll('.aic__scroller').forEach(initScroller);
    s.querySelectorAll('[data-aic-card]').forEach(initFlip);
  }

  function editorCard(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    var card = t.closest('[data-aic-card]');
    if (card) initFlip(card);
    return card && card.dataset.aicFlipInit === '1' ? card : null;
  }

  window.AquonIngredientCards = {
    init: initAll,
    open: function (card) { if (card) setOpen(card, true, { focus: false }); },
    close: function (card) { if (card) setOpen(card, false, { focus: false }); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-rendered sections are new nodes; bind them once.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  // Theme editor: show the selected block's detail face without moving focus.
  document.addEventListener('shopify:block:select', function (e) {
    var card = editorCard(e);
    if (card) setOpen(card, true, { focus: false, instant: true });
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var card = editorCard(e);
    if (card) setOpen(card, false, { focus: false, instant: true });
  });
})();
